import React, { useRef, useState } from 'react';
import { IMAGES } from '../../constants/images';
import { createPortal } from 'react-dom';

interface ImageThumbnailProps {
  imageUrl?: string;
  altText?: string;
  isLoading?: boolean;
  error?: string;
}

const ImageThumbnail: React.FC<ImageThumbnailProps> = ({
  imageUrl,
  altText,
  isLoading,
  error,
}) => {
  const thumbRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // 툴팁 위치 계산 함수
  const handleMouseEnter = () => {
    if (thumbRef.current) {
      const rect = thumbRef.current.getBoundingClientRect();
      setTooltipPos({
        left: rect.left + rect.width / 2,
        top: rect.bottom + 8, // 썸네일 하단에서 약간 띄움
      });
      setShowTooltip(true);
    }
  };
  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  if (!imageUrl) {
    return (
      <div className="flex items-center justify-center flex-shrink-0 w-full h-32 bg-gray-100 rounded">
        <span className="text-sm text-gray-400">이미지 없음</span>
      </div>
    );
  }

  return (
    <div
      className="relative w-full overflow-visible group"
      ref={thumbRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 1) 이미지 전용 래퍼: overflow-hidden */}
      <div className="relative w-full h-32 bg-gray-100 rounded">
        <div className="overflow-hidden w-full h-full flex items-center justify-center">
          <img
            src={imageUrl}
            alt="미리보기"
            className="object-contain w-full h-full"
            onError={(e) => {
              e.currentTarget.src = IMAGES.defaultThumbnail;
            }}
          />
        </div>

        {isLoading && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-white bg-opacity-70">
            <svg
              className="w-5 h-5 text-indigo-500 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
        )}
      </div>

      {/* 2) 툴팁: Portal로 분리 */}
      {!isLoading &&
        altText &&
        showTooltip &&
        tooltipPos &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            className="z-[9999] w-60 px-3 py-2 text-xs text-center text-gray-700 bg-white border border-gray-300 rounded shadow transition-opacity duration-200"
            style={{
              position: 'fixed',
              left: tooltipPos.left,
              top: tooltipPos.top,
              transform: 'translateX(-50%)',
              whiteSpace: 'normal',
              pointerEvents: 'auto',
            }}
          >
            {altText}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ImageThumbnail;
